import React, { useState, useEffect } from 'react';
import { Container, Grid, GridItem, Accordion, Heading, Body, Box, Link } from '@iris/react-components';
import { sendClick } from 'reaxl-analytics';
import ChangeStateFooter from './ChangeStateFooter';
import BlpAccordionPanel from './BlpAccordionPanel';
import { FEES_ACCORDION_COPY } from '@/constants/feesAccordionData';
import { SMALL_FEE_HASH } from '@/constants/landingPageAnchors';
import { CheckoutOptionsModal } from '@psx/react-components';

type FeesAndTaxesSectionProps = {
  currentZip?: string;
  onZipChange?: (zip: string) => Promise<void>;
  eligibility?: boolean;
  locationData?: {
    zip?: string;
    stateName?: string;
    city?: string;
    county?: string;
  } | null;
};

const VEHICLE_PURCHASE_CHECKOUT_ACCORDION_VALUE = 'vehicle-purchase-checkout';

const CHECKOUT_TYPE_LABELS: {
  label: string;
  introKey: 'fullServiceIntro' | 'securePayIntro';
  itemsKey: 'fullServiceItems' | 'securePayItems';
}[] = [
  { label: 'Full Service Checkout', introKey: 'fullServiceIntro', itemsKey: 'fullServiceItems' },
  { label: 'Secure Pay Checkout', introKey: 'securePayIntro', itemsKey: 'securePayItems' },
];

export default function FeesAndTaxesSection({
  currentZip = '',
  onZipChange,
  eligibility = false,
  locationData = null,
}: FeesAndTaxesSectionProps) {
  const [selectedState, setSelectedState] = useState('');
  const [isCheckoutOptionsModalOpen, setIsCheckoutOptionsModalOpen] = useState(false);
  const [openFeesAccordionValues, setOpenFeesAccordionValues] = useState<string[]>([]);

  useEffect(() => {
    if (locationData && locationData.stateName) {
      setSelectedState(locationData.stateName);
    }
  }, [locationData]);

  useEffect(() => {
    const openAccordionIfSmallFeeHash = () => {
      if (window.location.hash === SMALL_FEE_HASH) {
        setOpenFeesAccordionValues((current) =>
          current.includes(VEHICLE_PURCHASE_CHECKOUT_ACCORDION_VALUE)
            ? current
            : [...current, VEHICLE_PURCHASE_CHECKOUT_ACCORDION_VALUE]
        );
      }
    };

    openAccordionIfSmallFeeHash();
    window.addEventListener('hashchange', openAccordionIfSmallFeeHash);
    return () => window.removeEventListener('hashchange', openAccordionIfSmallFeeHash);
  }, []);

  const handleSaveState = async (sanitizedZip: string) => {
    await onZipChange?.(sanitizedZip);
  };

  const handleCheckoutOptionsClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    const pixallData = {
      hasListingType: false,
      label: 'text-link::learn-more-about-our-checkout-options',
      eventSource: 'link',
      pageId: 'slp_psx_buy',
      cdl: {
        event: {
          action: 'Click',
          element: 'Learn More About Our Checkout Options Link',
          tagIndex: 'psx-123',
        },
      },
    };
    sendClick('sycClickEvent', event, {
      isPixallEvent: true,
      pixallData,
    });

    setIsCheckoutOptionsModalOpen(true);
  };

  const handleCloseCheckoutOptionsModal = () => {
    setIsCheckoutOptionsModalOpen(false);
  };

  const feesAccordionCopy = FEES_ACCORDION_COPY[eligibility ? 'eligible' : 'ineligible'];

  return (
    <>
      <Box className="fees-and-taxes-section" data-testid="fees-and-taxes-section">
        <Container className="fees-container iris:!mx-0">
          <Box className="fees-wrapper">
            <Box className="fees-header" id={SMALL_FEE_HASH.slice(1)}>
              <Heading size="xl" as="h2">How Much Do I Have to Pay?</Heading>
              <Body size="md" as="p">
                When buying a car with Autotrader Private Seller Exchange, the fees you pay will differ based on the state you'll be registering the car.*
              </Body>
            </Box>

            {/* iris-override (token mismatch): Grid gap="xl" token value does not match design spec column spacing; using iris:!gap-x */}
            <Grid columns={{ xs: 1, md: 2 }} gap="xl" className="iris:!gap-x-[40px] justify-center">
              {/* Required by Law Section */}
              <GridItem className="iris:p-0">
                <Box className="fees-card max-[767px]:!mb-0" data-testid="fees-card-required-by-law">
                  <Heading size="md" as="h3">Required by Law</Heading>
                  <Body size="md" as="p">
                    Whether you purchase a car from a dealership, private party, or family member, you must pay the following taxes and fees:
                  </Body>

                  <Accordion variant="uncontained" defaultValue={[]} className="fees-accordion">
                    <BlpAccordionPanel
                      value="sales-tax"
                      title="Sales Tax"
                      itemClassName="fees-accordion-item"
                      triggerTestId="fees-accordion-panel-sales-tax"
                    >
                      <Body size="md" as="p">
                        Most states require a buyer to pay sales tax based on the fair market price of the vehicle. If the reported sale price and fair market price are significantly different, you will most likely need to pay taxes on the fair market price.
                        <br />
                        You can use kbb.com to find a vehicle's value.
                      </Body>
                    </BlpAccordionPanel>

                    <BlpAccordionPanel
                      value="registration-fee"
                      title="Registration Fee"
                      itemClassName="fees-accordion-item"
                      triggerTestId="fees-accordion-panel-registration-fee"
                    >
                      Fee for registering the vehicle with your state's DMV.
                    </BlpAccordionPanel>

                    <BlpAccordionPanel
                      value="title-transfer-fee"
                      title="Title Transfer Fee"
                      itemClassName="fees-accordion-item"
                      triggerTestId="fees-accordion-panel-title-transfer-fee"
                    >
                      Fee for officially transferring the vehicle's title from the seller to the buyer.
                    </BlpAccordionPanel>
                  </Accordion>
                </Box>
              </GridItem>

              {/* Autotrader Private Seller Exchange Fees Section */}
              <GridItem>
                <Box className="fees-card" data-testid="fees-card-psx-fees">
                  <Heading size="md" as="h3">Autotrader Private Seller Exchange Fees</Heading>
                  <Body size="md" as="p">
                    We offer two types of checkout when you purchase a vehicle for sale. The fee you pay depends on which option you choose (and your state&apos;s requirements).
                  </Body>

                  <Accordion
                    variant="uncontained"
                    value={openFeesAccordionValues}
                    onValueChange={setOpenFeesAccordionValues}
                    className="fees-accordion"
                  >
                    <BlpAccordionPanel
                      value={VEHICLE_PURCHASE_CHECKOUT_ACCORDION_VALUE}
                      title="Vehicle Purchase Checkout Options"
                      itemClassName="fees-accordion-item"
                      triggerTestId="fees-accordion-panel-vehicle-purchase-checkout"
                    >
                      {CHECKOUT_TYPE_LABELS.map(({ label, introKey, itemsKey }) => (
                        <React.Fragment key={label}>
                          <ul>
                            <li>
                              <span className="iris:font-bold">{label}</span>
                              {' '}
                              <span>- {feesAccordionCopy[introKey]}</span>
                            </li>
                          </ul>
                          <div className="iris:ml-[20px]">
                            <ul>
                              {feesAccordionCopy[itemsKey].map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </React.Fragment>
                      ))}
                      <Link
                        className="iris:inline-flex iris:text-[14px] iris:md:text-[16px] iris:!text-(--base-color-primary-600) hover:iris:!text-(--base-color-primary-600) active:iris:!text-(--base-color-primary-600)"
                        onClick={handleCheckoutOptionsClick}
                      >
                        Learn more about our checkout options
                      </Link>
                    </BlpAccordionPanel>
                  </Accordion>
                </Box>
              </GridItem>
            </Grid>

            <ChangeStateFooter
              text="*These are the services provided if you register the vehicle in"
              selectedState={selectedState}
              currentZip={currentZip}
              onSave={handleSaveState}
              modalText="Autotrader Private Seller Exchange services differ based on what state you are registering the car. Enter the ZIP code where you are registering your vehicle."
            />
          </Box>
        </Container>
      </Box>

      <CheckoutOptionsModal
        isOpen={isCheckoutOptionsModalOpen}
        onClose={handleCloseCheckoutOptionsModal}
      />
    </>
  );
}
